import type { PharmacyOrder } from '@/types/clinicalAdmin';

export interface OrderGroup {
  key: string;
  productId: string;
  purchaseKind: 'subscription' | 'one-time' | 'unknown';
  purchaseId: string | null;
  purchaseIdShort: string | null;
  current: PharmacyOrder[];
  history: PharmacyOrder[];
}

/** Canonical treatment program for display grouping. A tirzepatide purchase
 * starts as the root billing SKU and becomes a clinician-selected dose tier. */
function programProductId(order: PharmacyOrder): string {
  const meta = (order.metadata as Record<string, unknown> | null) || {};
  const programId =
    typeof meta.program_product_id === 'string'
      ? meta.program_product_id.trim()
      : '';
  return programId || order.product_id || '—';
}

export function productGroupLabel(productId: string): string {
  return productId === 'tirzepatide-program'
    ? 'Tirzepatide program'
    : productId;
}

function purchaseIdentity(order: PharmacyOrder): {
  key: string;
  kind: OrderGroup['purchaseKind'];
  id: string | null;
  idShort: string | null;
} {
  const meta = (order.metadata as Record<string, unknown> | null) || {};
  const subscriptionId =
    typeof meta.stripe_subscription_id === 'string'
      ? meta.stripe_subscription_id
      : '';
  if (subscriptionId) {
    return {
      key: `sub:${subscriptionId}`,
      kind: 'subscription',
      id: subscriptionId,
      idShort: subscriptionId.slice(-6),
    };
  }
  const sessionId =
    typeof meta.stripe_session_id === 'string' ? meta.stripe_session_id : '';
  const normalized = sessionId.replace(/^superseded:/, '');
  if (normalized) {
    return {
      key: `sess:${normalized}`,
      kind: 'one-time',
      id: normalized,
      idShort: normalized.slice(-6),
    };
  }
  return {
    key: `order:${order.id}`,
    kind: 'unknown',
    id: order.id,
    idShort: order.id.slice(0, 6),
  };
}

/** Follow replacement links so operational resends inherit the purchase and
 * treatment identity of the row they ultimately became. */
function terminalSuccessor(
  order: PharmacyOrder,
  byId: Map<string, PharmacyOrder>,
): PharmacyOrder {
  let current = order;
  const seen = new Set<string>([current.id]);
  for (;;) {
    const meta =
      (current.metadata as Record<string, unknown> | null) || {};
    const nextId = typeof meta.replaced_by === 'string' ? meta.replaced_by : '';
    if (!nextId || seen.has(nextId)) return current;
    const next = byId.get(nextId);
    if (!next) return current;
    seen.add(next.id);
    current = next;
  }
}

/** Group by treatment program + purchase identity. Superseded rows remain as
 * collapsible history in the same timeline. */
export function groupOrdersByProduct(orders: PharmacyOrder[]): OrderGroup[] {
  const byKey = new Map<string, OrderGroup>();
  const byId = new Map(orders.map((order) => [order.id, order]));
  for (const order of orders) {
    const successor = terminalSuccessor(order, byId);
    const identity = purchaseIdentity(successor);
    const productId = programProductId(successor);
    const key = `${productId}|${identity.key}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        productId,
        purchaseKind: identity.kind,
        purchaseId: identity.id,
        purchaseIdShort: identity.idShort,
        current: [],
        history: [],
      };
      byKey.set(key, group);
    }
    if (order.status === 'superseded') group.history.push(order);
    else group.current.push(order);
  }

  const byCreatedDesc = (a: PharmacyOrder, b: PharmacyOrder) =>
    (b.created_at || '').localeCompare(a.created_at || '');
  const groups = Array.from(byKey.values());
  for (const group of groups) {
    group.current.sort(byCreatedDesc);
    group.history.sort(byCreatedDesc);
  }
  groups.sort((a, b) => {
    const aIssue = a.current.some(
      (order) => order.status === 'failed' && order.partner_key === 'unrouted',
    );
    const bIssue = b.current.some(
      (order) => order.status === 'failed' && order.partner_key === 'unrouted',
    );
    if (aIssue !== bIssue) return aIssue ? -1 : 1;
    const aLatest = a.current[0]?.created_at || a.history[0]?.created_at || '';
    const bLatest = b.current[0]?.created_at || b.history[0]?.created_at || '';
    return bLatest.localeCompare(aLatest);
  });
  return groups;
}

function normalizedCycleIdentity(order: PharmacyOrder): string {
  const meta = (order.metadata as Record<string, unknown> | null) || {};
  const invoiceId =
    typeof meta.stripe_invoice_id === 'string' ? meta.stripe_invoice_id : '';
  if (invoiceId) return `invoice:${invoiceId.replace(/^superseded:/, '')}`;
  const sessionId =
    typeof meta.stripe_session_id === 'string' ? meta.stripe_session_id : '';
  if (sessionId) return `session:${sessionId.replace(/^superseded:/, '')}`;
  return `order:${order.id}`;
}

/** Superseded and replacement rows share a normalized invoice id, so retrying
 * a billing cycle does not appear as another renewal. */
export function orderCycleLabels(group: OrderGroup): Map<string, string> {
  const labels = new Map<string, string>();
  if (group.purchaseKind !== 'subscription') return labels;
  const rows = [...group.current, ...group.history].sort((a, b) =>
    (a.created_at || '').localeCompare(b.created_at || ''),
  );
  const cycleNumbers = new Map<string, number>();
  for (const order of rows) {
    const cycleId = normalizedCycleIdentity(order);
    let cycle = cycleNumbers.get(cycleId);
    if (cycle === undefined) {
      cycle = cycleNumbers.size;
      cycleNumbers.set(cycleId, cycle);
    }
    labels.set(order.id, cycle === 0 ? 'Initial purchase' : `Renewal ${cycle}`);
  }
  return labels;
}
